import React from 'react';
import { Button } from './ui/button';
import { ChefHat, Users, Clock, Award, Heart, TrendingUp } from 'lucide-react';

const Careers = () => {
  const positions = [
    {
      icon: ChefHat,
      title: 'Professional Cooks & Chefs',
      description: 'Experienced cooks and chefs to prepare high-quality food for our catering events and matchday services.',
      requirements: [
        'Proven cooking experience',
        'Food hygiene certificate (Level 2 minimum)',
        'Ability to work under pressure',
        'Team player with positive attitude'
      ]
    },
    {
      icon: Users,
      title: 'Kitchen Staff',
      description: 'Kitchen assistants to support our team with food preparation, cleaning, and general kitchen duties.',
      requirements: [
        'Basic food preparation skills',
        'Reliable and punctual',
        'Willing to learn',
        'Good work ethic'
      ]
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Flexible Hours',
      description: 'Event-based work with flexible scheduling to suit your lifestyle'
    },
    {
      icon: Award,
      title: 'Professional Growth',
      description: 'Opportunities to develop your skills and grow within our team'
    },
    {
      icon: Heart,
      title: 'Great Team',
      description: 'Join a passionate team that takes pride in delivering quality food'
    },
    {
      icon: TrendingUp,
      title: 'Competitive Pay',
      description: 'Fair wages that reflect your skills and experience'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: 'var(--color-accent-coconut)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--text-base)' }}>
              Join Our Team
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
              Be part of a professional catering team serving Greater Manchester
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/35041664/pexels-photo-35041664.jpeg"
                alt="Our professional catering team"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold" style={{ color: 'var(--text-base)' }}>
                Why Work With Us?
              </h2>
              <p className="text-lg" style={{ color: 'var(--text-subtle-light)' }}>
                At Marlowes Catering, we're always looking for talented and passionate individuals to join our growing team. Whether you're an experienced chef or starting your career in catering, we offer a supportive environment where you can develop your skills.
              </p>
              <p className="text-lg" style={{ color: 'var(--text-subtle-light)' }}>
                Our team caters for a diverse range of events from corporate functions to matchday catering for local football clubs. Every day brings new challenges and opportunities to showcase your talents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              Current Opportunities
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
              We're looking for dedicated professionals to join our catering team
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {positions.map((position, index) => {
              const Icon = position.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg border-2 transition-all card-hover"
                  style={{ borderColor: 'var(--color-accent-coconut)' }}
                >
                  <div className="mb-6">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: 'var(--color-accent-coconut)' }}
                    >
                      <Icon size={32} style={{ color: 'var(--accent-ui-accent)' }} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-base)' }}>
                      {position.title}
                    </h3>
                    <p className="mb-4" style={{ color: 'var(--text-subtle-light)' }}>
                      {position.description}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-bold mb-3" style={{ color: 'var(--text-base)' }}>
                      What We're Looking For:
                    </h4>
                    <ul className="space-y-2">
                      {position.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start">
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full mt-2 mr-2"
                            style={{ backgroundColor: 'var(--accent-ui-accent)' }}
                          ></span>
                          <span style={{ color: 'var(--text-subtle-light)' }}>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={() => document.getElementById('apply-section').scrollIntoView({ behavior: 'smooth' })}
                    className="w-full"
                    style={{ backgroundColor: 'var(--accent-ui-accent)', color: 'white' }}
                  >
                    Apply for This Position
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--color-accent-coconut)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              What We Offer
            </h2>
            <p className="text-xl" style={{ color: 'var(--text-subtle-light)' }}>
              Join a team that values your contribution
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 text-center shadow-lg">
                  <div className="flex justify-center mb-4">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-accent-coconut)' }}
                    >
                      <Icon size={28} style={{ color: 'var(--accent-ui-accent)' }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-base)' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-subtle-light)' }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              Meet The Team
            </h2>
            <p className="text-xl" style={{ color: 'var(--text-subtle-light)' }}>
              Our dedicated professionals making every event a success
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://images.pexels.com/photos/35407775/pexels-photo-35407775.jpeg"
                alt="Team member preparing food"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://images.pexels.com/photos/35041664/pexels-photo-35041664.jpeg"
                alt="Chef at work with curry"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://images.pexels.com/photos/35382686/pexels-photo-35382686.jpeg"
                alt="Kitchen staff"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application CTA */}
      <section id="apply-section" className="py-20" style={{ backgroundColor: 'var(--surface-background-dark)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Join Us?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We'd love to hear from you! Whether you're an experienced professional or looking to start your career in catering, get in touch to discuss opportunities with Marlowes Catering.
          </p>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              How to Apply
            </h3>
            <div className="text-left text-white/90 space-y-4 max-w-2xl mx-auto">
              <p>
                <strong>1. Contact Us:</strong> Use the contact form on our website or call us directly
              </p>
              <p>
                <strong>2. Tell Us About You:</strong> Share your experience, qualifications, and why you want to join our team
              </p>
              <p>
                <strong>3. Include:</strong> Your CV/resume and any relevant certificates (food hygiene, etc.)
              </p>
              <p>
                <strong>4. We'll Respond:</strong> We review all applications and will be in touch to discuss next steps
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              size="lg"
              onClick={() => {
                window.location.href = '/#contact';
              }}
              className="text-xl px-12 py-8"
              style={{ backgroundColor: 'var(--color-accent-lime)', color: 'var(--text-base)' }}
            >
              Apply Now via Contact Form
            </Button>
            <p className="text-white/80">
              Or call us directly on <a href="tel:01612228831" className="text-lime-400 font-bold hover:underline">0161 222 8831</a>
            </p>
            <p className="text-white/80">
              Email: <a href="mailto:info@marlowescatering.co.uk" className="text-lime-400 font-bold hover:underline">info@marlowescatering.co.uk</a>
            </p>
          </div>
        </div>
      </section>

      {/* Equal Opportunities Statement */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm" style={{ color: 'var(--text-subtle-light)' }}>
            Marlowes Catering is an equal opportunities employer. We welcome applications from all qualified candidates regardless of age, gender, ethnicity, disability, or background.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Careers;
